import { useState } from 'react';

import { useDrawdownRepaymentSchedule } from '@api/lendingAPI/lapiHooks';
import {
    TFormalOffer,
    TFormalOffers
} from '@helpers/FormalOffers/formalOffers.types';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { Select } from '@iwoca/orion';
import { Text } from '@iwoca/orion-ui';
import { Skeleton } from '@ui';
import { FormattedMessage } from 'react-intl';

import { MoneyWithDecimals } from '../../../../../../ui/moneyWithDecimals';
import { Rate } from '../../../../../../ui/rate/Rate';

const Duration = ({
    duration
}: {
    duration: NonNullable<TFormalOffer['duration']>;
}) => (
    <FormattedMessage
        id="_.compare_offers_table.duration_months"
        defaultMessage="{months} months"
        values={{ months: getDurationInMonths(duration) }}
    />
);

const MoneyCell = ({
    value,
    isLoaded
}: {
    value: number | undefined;
    isLoaded: boolean;
}) =>
    isLoaded ? (
        <MoneyWithDecimals value={value} />
    ) : (
        <Skeleton className="h-[20px] w-[80px]" data-testid="cell-skeleton" />
    );

const Row = ({
    label,
    leftColumn,
    rightColumn
}: {
    label: React.ReactNode;
    leftColumn: React.ReactNode;
    rightColumn: React.ReactNode;
}) => (
    <div className="flex flex-col border-b border-secondary-90 last:border-0 min-m:flex-row">
        <div className="flex w-full items-center justify-center border-b border-secondary-90 p-l pr-0 text-s min-m:w-1/3 min-m:justify-start min-m:border-b-0">
            {label}
        </div>

        <div className="flex w-full min-m:w-2/3">
            <div
                className={`flex w-1/2 items-center justify-center border-r border-secondary-90 p-l text-s min-m:justify-end min-m:border-r-0`}
            >
                {leftColumn}
            </div>
            <div
                className={`flex w-1/2 items-center justify-center p-l text-s min-m:justify-end`}
            >
                {rightColumn}
            </div>
        </div>
    </div>
);

const OfferDurationDropdown = ({
    value,
    onChange,
    formalOffers
}: {
    value: string;
    onChange: (offerId: string) => void;
    formalOffers: TFormalOffers;
}) => (
    <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        wrapperClassName="w-full bg-structure-100 rounded-m"
    >
        {formalOffers.map((offer) => (
            <option key={offer.offer_id} value={offer.offer_id}>
                <Duration duration={offer.duration!} />
            </option>
        ))}
    </Select>
);

const useOfferScheduleData = (offerId: string, drawdownAmount: number) => {
    const { data, isSuccess, isError } = useDrawdownRepaymentSchedule({
        offerId,
        drawdownAmount,
        enabled: Boolean(offerId)
    });

    const summary = data?.summary;

    return {
        isLoaded: isSuccess,
        isError,
        minMonthlyRepayment:
            summary?.simplified_schedule?.equal_repayments?.amount,
        fees: summary?.total_fees,
        totalRepayment: summary?.total_payable
    };
};

export const CompareOffersTable = ({
    formalOffers,
    drawdownAmount
}: {
    formalOffers: TFormalOffers;
    drawdownAmount: number;
}) => {
    const [leftOfferId, setLeftOfferId] = useState(formalOffers[0].offer_id!);
    const [rightOfferId, setRightOfferId] = useState(formalOffers[1].offer_id!);

    const leftOffer = formalOffers.find(
        (offer) => offer.offer_id === leftOfferId
    )!;
    const rightOffer = formalOffers.find(
        (offer) => offer.offer_id === rightOfferId
    )!;

    const leftOfferSchedule = useOfferScheduleData(leftOfferId, drawdownAmount);
    const rightOfferSchedule = useOfferScheduleData(
        rightOfferId,
        drawdownAmount
    );

    const hasMultipleFormalOffers = formalOffers.length > 2;
    const hasError = leftOfferSchedule.isError || rightOfferSchedule.isError;

    return (
        <>
            <div className="mt-6 overflow-hidden rounded-m border border-secondary-90">
                <div className="flex items-center border-b border-secondary-90 bg-secondary-95">
                    <div className="hidden p-l text-xs font-bold text-structure-40 min-m:flex min-m:w-1/3">
                        <FormattedMessage
                            id="_.compare_offers_table.compare_label"
                            defaultMessage="COMPARE"
                        />
                    </div>
                    {hasMultipleFormalOffers && (
                        <div className="flex w-full min-m:w-2/3">
                            <div className="flex w-1/2 items-center justify-end p-l">
                                <OfferDurationDropdown
                                    value={leftOfferId}
                                    onChange={setLeftOfferId}
                                    formalOffers={formalOffers}
                                />
                            </div>
                            <div className="flex w-1/2 items-center justify-end p-l">
                                <OfferDurationDropdown
                                    value={rightOfferId}
                                    onChange={setRightOfferId}
                                    formalOffers={formalOffers}
                                />
                            </div>
                        </div>
                    )}
                </div>

                <div className="text-sm min-m:text-base">
                    <Row
                        label={
                            <FormattedMessage
                                id="_.compare_offers_table.full_term"
                                defaultMessage="Full term"
                            />
                        }
                        leftColumn={<Duration duration={leftOffer.duration!} />}
                        rightColumn={
                            <Duration duration={rightOffer.duration!} />
                        }
                    />
                    <Row
                        label={
                            <FormattedMessage
                                id="_.compare_offers_table.min_repayment"
                                defaultMessage="Min. monthly repayment"
                            />
                        }
                        leftColumn={
                            <MoneyCell
                                value={leftOfferSchedule.minMonthlyRepayment}
                                isLoaded={leftOfferSchedule.isLoaded}
                            />
                        }
                        rightColumn={
                            <MoneyCell
                                value={rightOfferSchedule.minMonthlyRepayment}
                                isLoaded={rightOfferSchedule.isLoaded}
                            />
                        }
                    />
                    <Row
                        label={
                            <FormattedMessage
                                id="_.compare_offers_table.interest_rate"
                                defaultMessage="Interest rate /30 days"
                            />
                        }
                        leftColumn={
                            <Rate value={leftOffer.interest_rates![0].rate!} />
                        }
                        rightColumn={
                            <Rate value={rightOffer.interest_rates![0].rate!} />
                        }
                    />
                    <Row
                        label={
                            <FormattedMessage
                                id="_.drawdown_action_panel.topup_drawdown.repayment_schedule_summary.feesLabel"
                                defaultMessage="Fees"
                            />
                        }
                        leftColumn={
                            <MoneyCell
                                value={leftOfferSchedule.fees}
                                isLoaded={leftOfferSchedule.isLoaded}
                            />
                        }
                        rightColumn={
                            <MoneyCell
                                value={rightOfferSchedule.fees}
                                isLoaded={rightOfferSchedule.isLoaded}
                            />
                        }
                    />
                    <Row
                        label={
                            <FormattedMessage
                                id="_.drawdown_action_panel.topup_drawdown.repayment_schedule_summary.totalRepaymentLabel"
                                defaultMessage="Total repayment"
                            />
                        }
                        leftColumn={
                            <MoneyCell
                                value={leftOfferSchedule.totalRepayment}
                                isLoaded={leftOfferSchedule.isLoaded}
                            />
                        }
                        rightColumn={
                            <MoneyCell
                                value={rightOfferSchedule.totalRepayment}
                                isLoaded={rightOfferSchedule.isLoaded}
                            />
                        }
                    />
                </div>
            </div>
            {hasError && (
                <Text className="pt-l text-center text-s font-med text-core-40">
                    <FormattedMessage
                        id="_.compare_offers_table.error"
                        defaultMessage="Something went wrong. Please try again later."
                    />
                </Text>
            )}
        </>
    );
};
